/**
 * TriageProjectGroup — one overarching triage study's header + nested batch
 * rows, rendered by `NonFactoryCategory` for the "triage-validation"
 * category only (Task 13). Mirrors `ProjectWorklistGroup.tsx`'s nested-group
 * look (a `SquareList` header row + `.wl`-style nested batch rows), adapted
 * for flat `ProjectSummary` records — there's no factory enrollment state
 * here, just `project_id` + counts from `/api/labeling/projects`.
 *
 * The nested rows intentionally don't reuse `SquareRow` — same reason as
 * `ProjectWorklistGroup`: `SquareRow`'s fixed `px-5` padding has no room for
 * the `pl-8` nested indent.
 */

import { useProgressQuery, type ProjectSummary } from "../../../hooks/useLabelingQueries";
import { FaChevronRight } from "../../../utils/icons";
import { ProgressMeter, SquareList, SquareRow } from "./primitives";

/**
 * Worklist total_labels counts expected labels, not completed readings.
 * Use the same progress endpoint as the reader view. Do not poll every
 * batch while the overview is open; normal mount/focus refresh still applies.
 * Missing or failed progress stays unknown instead of implying completion.
 */
function CompletionCell({ project }: { project: ProjectSummary }) {
  const { data, isLoading, error } = useProgressQuery(project.project_id, {
    refetchInterval: false,
  });
  const total = data?.total_labels;
  if (isLoading || error || !data || !total || total <= 0) {
    return <span className="shrink-0 font-mono text-xs text-fg-muted">—</span>;
  }
  return <ProgressMeter done={data.completed_labels} total={total} display="percent" />;
}

interface TriageProjectGroupProps {
  /** Overarching study label, e.g. "a2z-Omni-Triage" (from `groupProjects`). */
  label: string;
  /** The study's individual batches (project ids) as full `ProjectSummary` records. */
  projects: ProjectSummary[];
  /** Called with the batch's `project_id` when a batch row is clicked. */
  onOpenBatch: (projectId: string) => void;
}

export function TriageProjectGroup({ label, projects, onOpenBatch }: TriageProjectGroupProps) {
  return (
    <SquareList>
      <SquareRow>
        <span aria-hidden="true" className="font-mono text-fg-faint">
          ◪
        </span>
        <span className="text-sm font-semibold text-fg">{label}</span>
        <span className="flex-1" />
        <span className="font-mono text-xs text-fg-muted">{`${projects.length} batches`}</span>
      </SquareRow>
      {projects.map((project) => (
        <button
          key={project.project_id}
          type="button"
          onClick={() => onOpenBatch(project.project_id)}
          className="flex w-full items-center gap-4 py-3.5 pl-8 pr-5 text-left transition hover:bg-white/2"
        >
          <div className="min-w-0 flex-1">
            <div className="truncate font-mono text-sm text-fg">{project.project_id}</div>
          </div>
          <CompletionCell project={project} />
          <FaChevronRight className="h-3 w-3 shrink-0 text-fg-muted" />
        </button>
      ))}
    </SquareList>
  );
}

export default TriageProjectGroup;
